import React, { useEffect, useState } from 'react';
import { useStore } from 'react-redux';

import { getBlobData } from '@helpers/fileUpload.helper';
import { useDownloadLogo } from '@hooks/apiHooks';
import { Skeleton, Typography, useTheme } from '@mui/material';
import { RootState } from '@store/rootReducer';

import { useT } from '../../hooks/useT';
import { OOControlModelInterface } from '../../interfaces';

type OOImageComponentProps = {
  controlModel: OOControlModelInterface;
};

export const OOImageComponent: React.FC<OOImageComponentProps> = ({ controlModel }: OOImageComponentProps) => {
  const { t } = useT('entry');
  const theme = useTheme();
  const store = useStore<RootState>();
  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined);

  const { data: logoResponse, isSuccess, isLoading } = useDownloadLogo(
    store.getState().tenant.tenantId,
    controlModel.imageSrc || '',
  );

  useEffect(() => {
    if (isSuccess && logoResponse && controlModel.imageSrc) {
      const { data, headers } = logoResponse;
      const url = getBlobData(data, headers['content-type']).imageUrl;
      setImgSrc(url);
    }
  }, [isSuccess, logoResponse, controlModel.imageSrc]);

  // Cleanup blob URL only on unmount
  useEffect(() => {
    return () => {
      if (imgSrc) {
        URL.revokeObjectURL(imgSrc);
      }
    };
  }, [imgSrc]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        minHeight: 225,
      }}
    >
      {controlModel?.imageSrc && (
        <div
          style={{
            width: `calc(30% + 32px)`,
            marginBottom: '32px',
          }}
        >
          {isLoading && (
            <div
              style={{
                height: 0,
                overflow: 'hidden',
                paddingTop: '100%',
                position: 'relative',
              }}
            >
              <Skeleton
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
                animation="wave"
                width="100%"
                variant="circular"
                component="div"
                height={150}
              />
            </div>
          )}
          {imgSrc && !isLoading && (
            <img
              style={{
                borderRadius: '50%',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
              src={imgSrc}
              alt="preview"
            />
          )}
        </div>
      )}
      <Typography
        align="center"
        style={{
          fontSize: '24px',
          fontFamily: theme.typography.fontFamily || 'Roboto',
          color: '#030c18',
          lineHeight: '32px',
          fontWeight: 400,
        }}
      >
        {t(controlModel.label)}
      </Typography>
      <Typography
        align="center"
        variant="body1"
        style={{
          fontSize: '14px',
          fontFamily: theme.typography.fontFamily || 'Roboto',
          color: '#030c18',
          lineHeight: '24px',
          fontWeight: 400,
        }}
      >
        {t(controlModel.description)}
      </Typography>
    </div>
  );
};
